import React from 'react';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip,
  ResponsiveContainer, ReferenceLine,
} from 'recharts';

// ─── Mock Data ────────────────────────────────────────────────────────────────

const MONTHS = ['Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb'];

const HISTORICAL: { month: string; lcr: number; nsfr: number }[] = [
  { month: 'Mar', lcr: 132.1, nsfr: 112.4 },
  { month: 'Apr', lcr: 138.5, nsfr: 113.8 },
  { month: 'May', lcr: 129.7, nsfr: 110.2 },
  { month: 'Jun', lcr: 141.2, nsfr: 115.6 },
  { month: 'Jul', lcr: 136.4, nsfr: 114.1 },
  { month: 'Aug', lcr: 143.8, nsfr: 116.9 },
  { month: 'Sep', lcr: 139.1, nsfr: 115.3 },
  { month: 'Oct', lcr: 147.3, nsfr: 117.4 },
  { month: 'Nov', lcr: 142.6, nsfr: 116.8 },
  { month: 'Dec', lcr: 150.2, nsfr: 119.2 },
  { month: 'Jan', lcr: 148.9, nsfr: 117.6 },
  { month: 'Feb', lcr: 145.5, nsfr: 118.1 },
];

const HQLA_BREAKDOWN = [
  { level: 'Level 1', description: 'Cash & Central Bank Reserves', amount: 1560, pct: 65, color: 'bg-rose-500' },
  { level: 'Level 2A', description: 'Government Bonds (≥85%)', amount: 600, pct: 25, color: 'bg-rose-300' },
  { level: 'Level 2B', description: 'High-quality equities (≥75%)', amount: 240, pct: 10, color: 'bg-rose-200' },
];

const OUTFLOW_CATEGORIES = [
  { category: 'Retail Deposits', rate: '5%', amount: 82.5, pct: 5, color: 'bg-slate-600' },
  { category: 'Unsecured Wholesale', rate: '35%', amount: 577.5, pct: 35, color: 'bg-blue-500' },
  { category: 'Secured Funding', rate: '25%', amount: 412.5, pct: 25, color: 'bg-indigo-500' },
  { category: 'Other Outflows', rate: '35%', amount: 577.5, pct: 35, color: 'bg-violet-500' },
];

const LCR_CURRENT = 145.5;
const NSFR_CURRENT = 118.1;
const LCR_PREV = 148.9;
const NSFR_PREV = 117.6;
const HQLA_TOTAL = 2400;
const NET_OUTFLOWS = 1650;
const ASF = 3200;
const RSF = 2710;

// ─── Custom Tooltip ───────────────────────────────────────────────────────────

function CustomTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-slate-900 text-white rounded-lg px-3 py-2 text-xs shadow-xl">
      <div className="font-semibold mb-1">{label}</div>
      {payload.map((p: any) => (
        <div key={p.name} className="flex gap-2">
          <span className="text-slate-400">{p.name.toUpperCase()}:</span>
          <span className="font-mono font-semibold">{p.value.toFixed(1)}%</span>
        </div>
      ))}
    </div>
  );
}

// ─── Ratio Card ───────────────────────────────────────────────────────────────

function RatioCard({
  label, value, prev, hqla, outflows, min = 100, color,
}: {
  label: string; value: number; prev: number; hqla?: number; outflows?: number;
  min?: number; color: string;
}) {
  const delta = value - prev;
  const utilPct = Math.min((value / 200) * 100, 100);
  const minLinePct = (min / 200) * 100;
  const isAbove = value >= min;

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6 flex-1">
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{label}</div>
          <div className={`text-4xl font-bold mt-1 ${isAbove ? 'text-slate-900' : 'text-red-600'}`}>
            {value.toFixed(1)}%
          </div>
        </div>
        <div className={`flex items-center gap-1 text-sm font-semibold ${delta >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
          <svg className={`w-4 h-4 ${delta < 0 ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 10l7-7m0 0l7 7m-7-7v18" />
          </svg>
          {Math.abs(delta).toFixed(1)}% vs Jan
        </div>
      </div>

      {/* Gauge bar */}
      <div className="mb-3">
        <div className="relative h-4 bg-slate-100 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all ${isAbove ? color : 'bg-red-500'}`}
            style={{ width: `${utilPct}%` }}
          />
          {/* Min line */}
          <div
            className="absolute top-0 bottom-0 w-0.5 bg-amber-400"
            style={{ left: `${minLinePct}%` }}
          />
        </div>
        <div className="flex justify-between text-xs text-slate-400 mt-1.5">
          <span>0%</span>
          <div className="flex items-center gap-1 text-amber-600 font-medium">
            <span>SFC Min: {min}%</span>
          </div>
          <span>200%</span>
        </div>
      </div>

      {/* Sub-metrics */}
      <div className="grid grid-cols-2 gap-3 mt-4 pt-4 border-t border-slate-100">
        {hqla !== undefined && (
          <>
            <div>
              <div className="text-xs text-slate-400">HQLA</div>
              <div className="text-sm font-semibold text-slate-800">${(hqla / 1000).toFixed(1)}B</div>
            </div>
            <div>
              <div className="text-xs text-slate-400">30d Net Outflows</div>
              <div className="text-sm font-semibold text-slate-800">${(outflows! / 1000).toFixed(2)}B</div>
            </div>
          </>
        )}
        {hqla === undefined && (
          <>
            <div>
              <div className="text-xs text-slate-400">Avail. Stable Funding</div>
              <div className="text-sm font-semibold text-slate-800">${(ASF / 1000).toFixed(1)}B</div>
            </div>
            <div>
              <div className="text-xs text-slate-400">Req. Stable Funding</div>
              <div className="text-sm font-semibold text-slate-800">${(RSF / 1000).toFixed(2)}B</div>
            </div>
          </>
        )}
        <div className="col-span-2">
          <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full ${
            isAbove ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
          }`}>
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d={isAbove ? 'M5 13l4 4L19 7' : 'M6 18L18 6M6 6l12 12'} />
            </svg>
            {isAbove ? `${(value - min).toFixed(1)}% above SFC minimum` : 'BELOW SFC MINIMUM'}
          </span>
        </div>
      </div>
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function CapitalAdequacyView() {
  return (
    <div className="flex flex-col h-full bg-slate-50 min-h-screen">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-rose-100 flex items-center justify-center">
            <svg className="w-4 h-4 text-rose-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
          </div>
          <div>
            <h1 className="text-lg font-semibold text-slate-900">Capital Adequacy — LCR & NSFR</h1>
            <p className="text-xs text-slate-500">Liquidity Coverage Ratio · Net Stable Funding Ratio — HKMA Basel III</p>
          </div>
          <div className="ml-auto">
            <span className="text-xs text-slate-400">Period: Feb 2026 </span>
            <span className="text-xs font-medium text-slate-600">· Reporting to HKMA</span>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-6 space-y-5">
        {/* Ratio Cards */}
        <div className="flex gap-5">
          <RatioCard
            label="Liquidity Coverage Ratio (LCR)"
            value={LCR_CURRENT}
            prev={LCR_PREV}
            hqla={HQLA_TOTAL}
            outflows={NET_OUTFLOWS}
            min={100}
            color="bg-rose-500"
          />
          <RatioCard
            label="Net Stable Funding Ratio (NSFR)"
            value={NSFR_CURRENT}
            prev={NSFR_PREV}
            min={100}
            color="bg-rose-400"
          />
        </div>

        {/* Trend Charts */}
        <div className="grid grid-cols-2 gap-5">
          {/* LCR Trend */}
          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-sm font-semibold text-slate-700">LCR — 12 Month Trend</div>
                <div className="text-xs text-slate-400">Liquidity Coverage Ratio (%)</div>
              </div>
              <div className="flex items-center gap-3 text-xs">
                <span className="flex items-center gap-1">
                  <span className="w-3 h-0.5 bg-rose-500 inline-block" />
                  LCR
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-3 h-0.5 bg-amber-400 border-dashed inline-block border-t-2 border-amber-400" />
                  SFC Min 100%
                </span>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={HISTORICAL} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="lcrGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#F43F5E" stopOpacity={0.15} />
                    <stop offset="95%" stopColor="#F43F5E" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                <YAxis domain={[90, 165]} tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                <Tooltip content={<CustomTooltip />} />
                <ReferenceLine y={100} stroke="#f59e0b" strokeDasharray="4 3" strokeWidth={1.5} />
                <Area
                  type="monotone"
                  dataKey="lcr"
                  stroke="#F43F5E"
                  strokeWidth={2}
                  fill="url(#lcrGrad)"
                  dot={false}
                  activeDot={{ r: 4, fill: '#F43F5E' }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* NSFR Trend */}
          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-sm font-semibold text-slate-700">NSFR — 12 Month Trend</div>
                <div className="text-xs text-slate-400">Net Stable Funding Ratio (%)</div>
              </div>
              <div className="flex items-center gap-3 text-xs">
                <span className="flex items-center gap-1">
                  <span className="w-3 h-0.5 bg-rose-400 inline-block" />
                  NSFR
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-3 h-0.5 bg-amber-400 inline-block" />
                  SFC Min 100%
                </span>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={HISTORICAL} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="nsfrGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#fb7185" stopOpacity={0.15} />
                    <stop offset="95%" stopColor="#fb7185" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                <YAxis domain={[100, 130]} tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                <Tooltip content={<CustomTooltip />} />
                <ReferenceLine y={100} stroke="#f59e0b" strokeDasharray="4 3" strokeWidth={1.5} />
                <Area
                  type="monotone"
                  dataKey="nsfr"
                  stroke="#fb7185"
                  strokeWidth={2}
                  fill="url(#nsfrGrad)"
                  dot={false}
                  activeDot={{ r: 4, fill: '#fb7185' }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Detail Tables */}
        <div className="grid grid-cols-2 gap-5">
          {/* HQLA Breakdown */}
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="px-5 py-3.5 border-b border-slate-200 bg-slate-50">
              <div className="text-sm font-semibold text-slate-700">HQLA Breakdown</div>
              <div className="text-xs text-slate-400">High-Quality Liquid Assets — Total ${(HQLA_TOTAL / 1000).toFixed(1)}B</div>
            </div>
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">Level</th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">Description</th>
                  <th className="text-right px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">Amount</th>
                  <th className="px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide w-28">Share</th>
                </tr>
              </thead>
              <tbody>
                {HQLA_BREAKDOWN.map(row => (
                  <tr key={row.level} className="border-b border-slate-100 last:border-0">
                    <td className="px-4 py-3">
                      <span className="text-xs font-bold text-rose-700 bg-rose-50 px-2 py-0.5 rounded">{row.level}</span>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">{row.description}</td>
                    <td className="px-4 py-3 text-right font-mono font-medium text-slate-800">${row.amount}M</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full ${row.color}`} style={{ width: `${row.pct}%` }} />
                        </div>
                        <span className="text-xs font-semibold text-slate-600 w-8 text-right">{row.pct}%</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-rose-50 border-t border-rose-100">
                  <td colSpan={2} className="px-4 py-3 text-sm font-semibold text-slate-700">Total HQLA</td>
                  <td className="px-4 py-3 text-right font-mono font-bold text-rose-700">${HQLA_TOTAL.toLocaleString()}M</td>
                  <td className="px-4 py-3 text-xs font-semibold text-rose-700 text-right">100%</td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Cash Outflow Categories */}
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="px-5 py-3.5 border-b border-slate-200 bg-slate-50">
              <div className="text-sm font-semibold text-slate-700">30-Day Cash Outflow Categories</div>
              <div className="text-xs text-slate-400">Net Stressed Outflows — Total ${(NET_OUTFLOWS / 1000).toFixed(2)}B</div>
            </div>
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">Category</th>
                  <th className="text-right px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">Run-off Rate</th>
                  <th className="text-right px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">Amount $M</th>
                  <th className="px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide w-28">Share</th>
                </tr>
              </thead>
              <tbody>
                {OUTFLOW_CATEGORIES.map(row => (
                  <tr key={row.category} className="border-b border-slate-100 last:border-0">
                    <td className="px-4 py-3 text-sm text-slate-700 font-medium">{row.category}</td>
                    <td className="px-4 py-3 text-right">
                      <span className="text-xs font-semibold text-slate-600 bg-slate-100 px-2 py-0.5 rounded">
                        {row.rate}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-slate-800">{row.amount.toFixed(0)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full ${row.color}`} style={{ width: `${row.pct}%` }} />
                        </div>
                        <span className="text-xs font-semibold text-slate-600 w-8 text-right">{row.pct}%</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-slate-50 border-t border-slate-200">
                  <td colSpan={2} className="px-4 py-3 text-sm font-semibold text-slate-700">Total Net Outflows</td>
                  <td className="px-4 py-3 text-right font-mono font-bold text-slate-800">{NET_OUTFLOWS.toLocaleString()}</td>
                  <td className="px-4 py-3 text-xs font-semibold text-slate-600 text-right">100%</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}